import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import pb from '../lib/pocketbase';
import { Calendar, Gauge, Fuel, Settings, ChevronLeft, ChevronRight, Phone, ArrowLeft, X } from 'lucide-react';

const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : null;
};

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [vehicle, setVehicle] = useState(null);
  const [similarVehicles, setSimilarVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        setLoading(true);
        const record = await pb.collection('car_listings').getOne(id);
        setVehicle(record);
        
        // Charger les véhicules similaires (même marque, excluant l'actuel)
        try {
          const similarRecords = await pb.collection('car_listings').getList(1, 3, {
            filter: `make = "${record.make}" && id != "${record.id}" && status = "available"`,
            sort: '-created'
          });
          
          if (similarRecords.items.length > 0) {
            setSimilarVehicles(similarRecords.items);
          } else {
            // Si pas de même marque, on prend les 3 derniers
            const otherRecords = await pb.collection('car_listings').getList(1, 3, {
              filter: `id != "${record.id}" && status = "available"`,
              sort: '-created'
            });
            setSimilarVehicles(otherRecords.items);
          }
        } catch (e) {
          console.error("Impossible de charger les véhicules similaires", e);
        }

        window.scrollTo(0, 0); // Remonter en haut de la page au chargement
      } catch (err) {
        console.error('Erreur lors du chargement du véhicule', err);
        setError("Ce véhicule n'est plus disponible ou n'existe pas.");
      } finally {
        setLoading(false);
      }
    };
    fetchVehicle();
  }, [id]);

  if (loading) {
    return (
      <div className="detail-page loading-page">
        <div className="loader"></div>
        <p>Chargement du véhicule...</p>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="detail-page error-page">
        <div className="error-icon">😕</div>
        <h2>Oups !</h2>
        <p>{error}</p>
        <button className="btn-back" onClick={() => navigate('/vente-auto')}>
          Retour aux annonces
        </button>
      </div>
    );
  }

  const images = [...(vehicle.cover_image ? [vehicle.cover_image] : []), ...(vehicle.images || [])];

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="vehicle-detail-page">
      <div className="container">
        
        <button className="back-link" onClick={() => navigate('/vente-auto')}>
          <ArrowLeft size={20} />
          Retour au catalogue
        </button>

        <div className="detail-layout">
          {/* Section Gauche : Galerie d'images */}
          <div className="detail-gallery">
            {images.length > 0 ? (
              <div className="main-image-container" onClick={() => setIsLightboxOpen(true)}>
                <img 
                  src={`https://christophedepanne.infocolab.online/api/files/car_listings/${vehicle.id}/${images[currentImageIndex]}`} 
                  alt={`${vehicle.make} ${vehicle.model}`} 
                  className="main-image"
                />
                {images.length > 1 && (
                  <>
                    <button className="nav-btn prev" onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}><ChevronLeft size={24} /></button>
                    <button className="nav-btn next" onClick={(e) => { e.stopPropagation(); handleNextImage(); }}><ChevronRight size={24} /></button>
                  </>
                )}
              </div>
            ) : (
              <div className="no-image">
                <div className="no-icon">🚗</div>
                <p>Aucune photo disponible</p>
              </div>
            )}
            
            {images.length > 1 && (
              <div className="thumbnails">
                {images.map((img, index) => (
                  <button 
                    key={index} 
                    className={`thumb-btn ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img 
                      src={`https://christophedepanne.infocolab.online/api/files/car_listings/${vehicle.id}/${img}?thumb=100x100`} 
                      alt={`Thumbnail ${index}`} 
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Section Droite : Informations */}
          <div className="detail-info">
            <span className="brand-badge">{vehicle.make} {vehicle.model}</span>
            <h1 className="vehicle-title">{vehicle.title}</h1>
            <div className="vehicle-price">{vehicle.price} €</div>
            
            <div className="specs-grid">
              <div className="spec-card">
                <Calendar size={22} />
                <div className="spec-text">
                  <span className="spec-label">Année</span>
                  <span className="spec-value">{vehicle.year}</span>
                </div>
              </div>
              <div className="spec-card">
                <Gauge size={22} />
                <div className="spec-text">
                  <span className="spec-label">Kilométrage</span>
                  <span className="spec-value">{vehicle.mileage} km</span>
                </div>
              </div>
              <div className="spec-card">
                <Fuel size={22} />
                <div className="spec-text">
                  <span className="spec-label">Carburant</span>
                  <span className="spec-value">{vehicle.fuel_type || '-'}</span>
                </div>
              </div>
              <div className="spec-card">
                <Settings size={22} />
                <div className="spec-text">
                  <span className="spec-label">Boîte</span>
                  <span className="spec-value">{vehicle.transmission === 'automatic' ? 'Auto' : 'Manuelle'}</span>
                </div>
              </div>
            </div>

            <div className="desc-section">
              <h3>Description du véhicule</h3>
              <p className="vehicle-desc">
                {vehicle.description || "Aucune description détaillée n'a été fournie pour ce véhicule."}
              </p>
            </div>

            {vehicle.youtube_link && getYoutubeEmbedUrl(vehicle.youtube_link) && (
              <div className="video-section">
                <h3>Vidéo du véhicule</h3>
                <div className="video-container">
                  <iframe 
                    src={getYoutubeEmbedUrl(vehicle.youtube_link)} 
                    title="Vidéo du véhicule" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen>
                  </iframe>
                </div>
              </div>
            )}

            <div className="detail-actions">
              <a href="tel:+33600000000" className="contact-btn shadow-lg">
                <Phone size={24} />
                Appeler pour ce véhicule
              </a>
              <div className="contact-note">
                <strong>Christophe Dépanne</strong> est à votre disposition pour programmer un essai ou répondre à vos questions.
              </div>
            </div>
          </div>
        </div>

        {/* Section Véhicules Similaires */}
        {similarVehicles.length > 0 && (
          <div className="similar-vehicles-section">
            <h2 className="similar-title">Ces véhicules pourraient aussi vous intéresser</h2>
            <div className="similar-grid">
              {similarVehicles.map(simCar => (
                <div 
                  key={simCar.id} 
                  className="sim-card"
                  onClick={() => navigate(`/vente-auto/${simCar.id}`)}
                >
                  <div className="sim-image-box">
                    {simCar.cover_image ? (
                      <img src={`https://christophedepanne.infocolab.online/api/files/car_listings/${simCar.id}/${simCar.cover_image}?thumb=300x200`} alt={simCar.model} />
                    ) : simCar.images && simCar.images.length > 0 ? (
                      <img src={`https://christophedepanne.infocolab.online/api/files/car_listings/${simCar.id}/${simCar.images[0]}?thumb=300x200`} alt={simCar.model} />
                    ) : (
                      <div className="sim-no-img">🚗</div>
                    )}
                    <div className="sim-price-badge">{simCar.price} €</div>
                  </div>
                  <div className="sim-info">
                    <h4 className="sim-title">{simCar.title}</h4>
                    <span className="sim-subtitle">{simCar.make} {simCar.model}</span>
                    <div className="sim-meta">
                      <span>{simCar.year}</span> • <span>{simCar.mileage} km</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox Plein Écran */}
      {isLightboxOpen && images.length > 0 && (
        <div className="lightbox-overlay" onClick={() => setIsLightboxOpen(false)}>
          <button className="lightbox-close" onClick={() => setIsLightboxOpen(false)}>
            <X size={32} color="white" />
          </button>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <img 
              src={`https://christophedepanne.infocolab.online/api/files/car_listings/${vehicle.id}/${images[currentImageIndex]}`} 
              alt={`${vehicle.make} ${vehicle.model}`} 
              className="lightbox-image"
            />
            {images.length > 1 && (
              <>
                <button className="lightbox-nav prev" onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}>
                  <ChevronLeft size={40} color="white" />
                </button>
                <button className="lightbox-nav next" onClick={(e) => { e.stopPropagation(); handleNextImage(); }}>
                  <ChevronRight size={40} color="white" />
                </button>
                <div className="lightbox-counter">
                  {currentImageIndex + 1} / {images.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        .vehicle-detail-page {
          padding: 120px 0 80px;
          background-color: #f8fafc;
          min-height: 100vh;
        }
        
        .loading-page, .error-page {
          padding: 150px 0;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 70vh;
          background-color: #f8fafc;
        }

        .loader { width: 50px; height: 50px; border: 4px solid #e2e8f0; border-top-color: #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 20px; }
        @keyframes spin { to { transform: rotate(360deg); } }

        .error-icon { font-size: 5rem; margin-bottom: 20px; }
        .error-page h2 { font-size: 2rem; color: #1e293b; margin-bottom: 15px; }
        .error-page p { color: #64748b; font-size: 1.2rem; margin-bottom: 30px; }
        .btn-back { background: #3b82f6; color: white; padding: 12px 24px; border-radius: 8px; border: none; font-weight: 700; cursor: pointer; transition: 0.3s; }
        .btn-back:hover { background: #2563eb; }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: transparent;
          border: none;
          color: #64748b;
          font-weight: 700;
          font-size: 1.05rem;
          cursor: pointer;
          margin-bottom: 30px;
          padding: 0;
          transition: 0.2s;
        }
        .back-link:hover { color: #3b82f6; transform: translateX(-5px); }

        .detail-layout {
          display: flex;
          flex-direction: row;
          gap: 40px;
          background: white;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.03);
          border: 1px solid #f1f5f9;
        }

        .detail-gallery {
          flex: 1.2;
          background: #f8fafc;
          padding: 30px;
          display: flex;
          flex-direction: column;
          gap: 20px;
          min-width: 0; /* Empêche le conteneur de s'élargir au-delà de sa taille avec beaucoup de miniatures */
        }

        .main-image-container {
          position: relative;
          width: 100%;
          height: 500px;
          border-radius: 20px;
          overflow: hidden;
          background: #e2e8f0;
          box-shadow: 0 10px 20px rgba(0,0,0,0.05);
          cursor: zoom-in;
        }

        .main-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }
        
        .main-image-container:hover .main-image {
          transform: scale(1.03);
        }

        .nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: rgba(255,255,255,0.9);
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #1e293b;
          transition: 0.2s;
          box-shadow: 0 4px 10px rgba(0,0,0,0.1);
        }
        .nav-btn:hover { background: white; transform: translateY(-50%) scale(1.1); }
        .nav-btn.prev { left: 20px; }
        .nav-btn.next { right: 20px; }

        .thumbnails {
          display: flex;
          gap: 15px;
          overflow-x: auto;
          padding-bottom: 10px;
        }

        .thumb-btn {
          width: 100px;
          height: 75px;
          border-radius: 12px;
          border: 3px solid transparent;
          overflow: hidden;
          cursor: pointer;
          padding: 0;
          flex-shrink: 0;
          opacity: 0.6;
          transition: 0.2s;
        }
        .thumb-btn.active { border-color: #3b82f6; opacity: 1; }
        .thumb-btn:hover { opacity: 1; }
        .thumb-btn img { width: 100%; height: 100%; object-fit: cover; }

        .no-image {
          height: 500px; display: flex; flex-direction: column; align-items: center; justify-content: center;
          color: #94a3b8; background: #e2e8f0; border-radius: 20px;
        }
        .no-icon { font-size: 5rem; margin-bottom: 15px; }

        .detail-info {
          flex: 1;
          padding: 40px 40px 40px 0;
          display: flex;
          flex-direction: column;
        }

        .brand-badge {
          display: inline-block;
          background: #eff6ff;
          color: #2563eb;
          padding: 8px 18px;
          border-radius: 50px;
          font-weight: 800;
          font-size: 0.95rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 20px;
          align-self: flex-start;
        }

        .vehicle-title {
          font-size: 2.8rem;
          font-weight: 900;
          color: #1e293b;
          margin-bottom: 15px;
          line-height: 1.1;
        }

        .vehicle-price {
          font-size: 2.5rem;
          font-weight: 900;
          color: #10b981;
          margin-bottom: 40px;
        }

        .specs-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 40px;
        }

        .spec-card {
          display: flex;
          align-items: center;
          gap: 15px;
          background: #f8fafc;
          padding: 20px;
          border-radius: 16px;
          color: #3b82f6;
          border: 1px solid #f1f5f9;
        }

        .spec-text { display: flex; flex-direction: column; }
        .spec-label { color: #64748b; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 1px; font-weight: 700; margin-bottom: 4px; }
        .spec-value { color: #1e293b; font-weight: 800; font-size: 1.1rem; }

        .desc-section { margin-bottom: 40px; }
        .desc-section h3 { font-size: 1.3rem; color: #1e293b; margin-bottom: 15px; font-weight: 800; }
        .vehicle-desc { color: #475569; line-height: 1.8; font-size: 1.1rem; white-space: pre-wrap; }

        .video-section { margin-bottom: 40px; }
        .video-section h3 { font-size: 1.3rem; color: #1e293b; margin-bottom: 15px; font-weight: 800; }
        .video-container { position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; border-radius: 16px; background: #e2e8f0; }
        .video-container iframe { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 16px; }

        .detail-actions {
          margin-top: auto;
          background: #f8fafc;
          padding: 30px;
          border-radius: 20px;
          border: 1px solid #f1f5f9;
        }

        .contact-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          background: #3b82f6;
          color: white;
          text-decoration: none;
          padding: 20px;
          border-radius: 16px;
          font-weight: 900;
          font-size: 1.2rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          transition: all 0.3s;
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.3);
        }

        .contact-btn:hover {
          background: #2563eb;
          transform: translateY(-3px);
          box-shadow: 0 15px 35px rgba(37, 99, 235, 0.4);
        }

        .contact-note {
          text-align: center;
          color: #64748b;
          font-size: 0.95rem;
          margin-top: 15px;
          line-height: 1.5;
        }

        /* Similaires */
        .similar-vehicles-section {
          margin-top: 80px;
          border-top: 1px solid #e2e8f0;
          padding-top: 60px;
        }

        .similar-title {
          font-size: 2rem;
          font-weight: 900;
          color: #1e293b;
          margin-bottom: 30px;
          text-align: center;
        }

        .similar-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 25px;
        }

        .sim-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
          cursor: pointer;
          transition: 0.3s;
          border: 1px solid #f1f5f9;
        }

        .sim-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px -5px rgba(0,0,0,0.1);
        }

        .sim-image-box {
          height: 180px;
          background: #f1f5f9;
          position: relative;
        }

        .sim-image-box img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .sim-no-img {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 3rem;
        }

        .sim-price-badge {
          position: absolute;
          bottom: 10px;
          right: 10px;
          background: rgba(30, 41, 59, 0.9);
          color: white;
          padding: 6px 12px;
          border-radius: 10px;
          font-weight: 800;
        }

        .sim-info {
          padding: 20px;
        }

        .sim-title {
          font-size: 1.1rem;
          font-weight: 900;
          color: #1e293b;
          margin: 0 0 4px 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sim-subtitle {
          color: #64748b;
          font-size: 0.8rem;
          text-transform: uppercase;
          font-weight: 800;
          letter-spacing: 1px;
          display: block;
          margin-bottom: 10px;
        }

        .sim-meta {
          color: #64748b;
          font-size: 0.9rem;
          font-weight: 500;
        }

        @media (max-width: 992px) {
          .detail-layout { flex-direction: column; }
          .detail-info { padding: 30px; }
          .main-image-container { height: 350px; }
        }
        
        @media (max-width: 768px) {
          .vehicle-title { font-size: 2.2rem; }
          .specs-grid { grid-template-columns: 1fr; }
          .lightbox-nav.prev { left: 10px; }
          .lightbox-nav.next { right: 10px; }
        }

        /* Lightbox Styles */
        .lightbox-overlay {
          position: fixed;
          top: 0; left: 0; width: 100vw; height: 100vh;
          background: rgba(0,0,0,0.95);
          z-index: 9999;
          display: flex; align-items: center; justify-content: center;
          backdrop-filter: blur(5px);
        }
        .lightbox-close {
          position: absolute;
          top: 30px; right: 30px;
          background: transparent; border: none; cursor: pointer; z-index: 10000;
          transition: transform 0.2s;
        }
        .lightbox-close:hover { transform: scale(1.2); }
        .lightbox-content {
          position: relative;
          max-width: 90vw; max-height: 90vh;
          display: flex; align-items: center; justify-content: center;
        }
        .lightbox-image {
          max-width: 100%; max-height: 85vh;
          object-fit: contain;
          border-radius: 8px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.5);
        }
        .lightbox-nav {
          position: absolute;
          top: 50%; transform: translateY(-50%);
          background: rgba(255,255,255,0.1); border: none; cursor: pointer;
          border-radius: 50%; width: 60px; height: 60px;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.2s;
        }
        .lightbox-nav:hover { background: rgba(255,255,255,0.3); }
        .lightbox-nav.prev { left: -80px; }
        .lightbox-nav.next { right: -80px; }
        .lightbox-counter {
          position: absolute; bottom: -40px; left: 50%; transform: translateX(-50%);
          color: rgba(255,255,255,0.8); font-size: 1.1rem; font-weight: 600; letter-spacing: 2px;
        }
      `}</style>
    </div>
  );
};

export default VehicleDetail;
